import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Code, Link as LinkIcon, Loader2, Copy, ExternalLink,
  CheckCircle2, AlertTriangle, Sparkles, Hash, TrendingUp,
  TrendingDown, Minus, ArrowDownToLine, Plus, Trash2,
} from "lucide-react";

const SCHEMA_TYPES = ["LocalBusiness", "Organization", "Product", "FAQPage", "Article"];

export default function SEO() {
  const [tab, setTab] = useState("keywords");

  return (
    <div className="space-y-6" data-testid="seo-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> AI SEO Engine
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">SEO Studio</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          AI-powered keyword research, schema.org JSON-LD generation, and broken-link detection — all in one place.
        </p>
      </header>

      <div className="flex gap-2 border-b border-white/5 overflow-x-auto">
        {[
          { id: "keywords", label: "Keyword research", icon: Hash },
          { id: "onpage", label: "On-page audit", icon: CheckCircle2 },
          { id: "rank", label: "Rank tracker", icon: TrendingUp },
          { id: "schema", label: "Schema generator", icon: Code },
          { id: "links", label: "Broken links", icon: LinkIcon },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`seo-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === t.id ? "border-indigo-500 text-white" : "border-transparent text-white/40 hover:text-white/70"}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "keywords" && <KeywordPanel />}
      {tab === "onpage" && <OnPagePanel />}
      {tab === "rank" && <RankPanel />}
      {tab === "schema" && <SchemaPanel />}
      {tab === "links" && <LinksPanel />}
    </div>
  );
}

function KeywordPanel() {
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    const { data } = await api.get("/seo/keywords/history");
    setHistory(data);
  };
  useEffect(() => { loadHistory(); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seo/keywords", { topic: topic.trim() || null });
      setData(data.data);
      toast.success("Keyword research generated");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="seo-keywords-panel">
      <div className="glass rounded-xl p-4 flex gap-2">
        <Search className="h-4 w-4 text-white/40 self-center" />
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Seed topic (leave blank to use your business profile)..."
          className="flex-1 bg-transparent text-sm outline-none"
          data-testid="seo-keyword-topic"
        />
        <button onClick={run} disabled={busy} data-testid="seo-keyword-run-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Research
        </button>
      </div>

      {data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Block title={`Primary keywords (${(data.primary_keywords || []).length})`}>
            {(data.primary_keywords || []).map((k, i) => (
              <div key={i} className="glass rounded-lg p-3 mb-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{k.keyword}</span>
                  <span className="text-xs text-white/40 font-mono">~{k.monthly_volume_estimate || "?"}/mo</span>
                </div>
                <div className="text-xs text-white/50 mt-1 flex flex-wrap gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300">{k.intent}</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">{k.difficulty}</span>
                </div>
                {k.why && <div className="text-xs text-white/40 mt-1">{k.why}</div>}
              </div>
            ))}
          </Block>
          <Block title={`Long-tail (${(data.long_tail || []).length})`}>
            {(data.long_tail || []).map((k, i) => (
              <div key={i} className="glass rounded-lg p-3 mb-2 text-sm">
                <div className="font-semibold">{k.keyword}</div>
                <div className="text-xs text-white/40 mt-1">{k.why}</div>
              </div>
            ))}
          </Block>
          <Block title={`Questions (${(data.questions || []).length})`}>
            {(data.questions || []).map((q, i) => (
              <div key={i} className="text-sm py-1.5 px-2">• {q}</div>
            ))}
          </Block>
          <Block title={`Content clusters (${(data.content_clusters || []).length})`}>
            {(data.content_clusters || []).map((c, i) => (
              <div key={i} className="glass rounded-lg p-3 mb-2 text-sm">
                <div className="font-semibold">{c.pillar}</div>
                <div className="text-xs text-white/50 mt-1">{(c.subtopics || []).join(" · ")}</div>
              </div>
            ))}
          </Block>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Past reports</div>
          <div className="space-y-1">
            {history.slice(0, 5).map((h) => (
              <button key={h.id} onClick={() => setData(h.data)}
                className="w-full glass rounded p-2 text-xs text-left flex justify-between hover:bg-white/[0.04]">
                <span className="truncate">{h.topic}</span>
                <span className="text-white/30 font-mono">{new Date(h.created_at).toLocaleDateString()}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function SchemaPanel() {
  const [type, setType] = useState("LocalBusiness");
  const [json, setJson] = useState('{}');
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);

  const sampleData = {
    LocalBusiness: { name: "", street: "", city: "", postal_code: "", phone: "", url: "" },
    Organization: { name: "", url: "", logo: "", description: "", social: [] },
    Product: { name: "", description: "", price: "", image: "" },
    FAQPage: { faqs: [{ q: "What is X?", a: "Y" }] },
    Article: { headline: "", description: "", author: "" },
  };
  useEffect(() => { setJson(JSON.stringify(sampleData[type], null, 2)); }, [type]);

  const run = async () => {
    let parsed;
    try { parsed = JSON.parse(json); } catch { toast.error("Invalid JSON"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/seo/schema", { type, data: parsed });
      setOut(data.schema);
      toast.success("Schema generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const snippet = out ? `<script type="application/ld+json">\n${JSON.stringify(out, null, 2)}\n</script>` : "";

  const copy = () => {
    navigator.clipboard.writeText(snippet);
    toast.success("Copied JSON-LD snippet");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="seo-schema-panel">
      <div className="glass rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-[0.2em] text-white/40">Type</span>
          <select value={type} onChange={(e) => setType(e.target.value)}
            data-testid="seo-schema-type" className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm">
            {SCHEMA_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <textarea rows={14} value={json} onChange={(e) => setJson(e.target.value)}
          data-testid="seo-schema-input"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono" />
        <button onClick={run} disabled={busy} data-testid="seo-schema-gen-btn"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Code className="h-4 w-4" />} Generate JSON-LD
        </button>
      </div>
      <div className="glass rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-[0.2em] text-white/40">JSON-LD snippet</span>
          {out && <button onClick={copy} data-testid="seo-schema-copy-btn"
            className="inline-flex items-center gap-1 text-xs glass rounded px-2 py-1 hover:bg-white/5"><Copy className="h-3 w-3" /> Copy</button>}
        </div>
        {out ? (
          <pre className="text-xs font-mono bg-black/40 rounded-lg p-3 overflow-x-auto max-h-96 whitespace-pre-wrap">{snippet}</pre>
        ) : (
          <div className="text-sm text-white/40 text-center py-10">Fill the inputs and click Generate.</div>
        )}
      </div>
    </div>
  );
}

function LinksPanel() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!url.startsWith("http")) { toast.error("Enter a full URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/seo/broken-links", { url, max_links: 30 });
      setOut(data);
      toast.success(`Checked ${data.checked} links`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="seo-links-panel">
      <div className="glass rounded-xl p-4 flex gap-2">
        <LinkIcon className="h-4 w-4 text-white/40 self-center" />
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://yoursite.com"
          className="flex-1 bg-transparent text-sm outline-none"
          data-testid="seo-links-url"
        />
        <button onClick={run} disabled={busy} data-testid="seo-links-run-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Scan
        </button>
      </div>

      {out && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Checked" value={out.checked} />
            <Kpi label="Broken" value={out.broken_count} accent={out.broken_count > 0 ? "rose" : "emerald"} />
            <Kpi label="Healthy" value={out.checked - out.broken_count} accent="emerald" />
          </div>
          <div className="glass rounded-xl divide-y divide-white/5">
            {out.results.map((r, i) => (
              <div key={i} className="p-3 flex items-center gap-3 text-sm" data-testid={`seo-link-result-${i}`}>
                {r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-rose-400 shrink-0" />}
                <span className={`font-mono text-xs ${r.ok ? "text-emerald-300" : "text-rose-300"}`}>{r.status}</span>
                <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-white/70 truncate flex-1 hover:text-white inline-flex items-center gap-1">
                  {r.url} <ExternalLink className="h-3 w-3 opacity-50" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="glass rounded-lg p-3">
      <div className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className={`font-heading text-2xl font-black mt-1 ${accent === "rose" ? "text-rose-300" : accent === "emerald" ? "text-emerald-300" : ""}`}>{value}</div>
    </div>
  );
}

function OnPagePanel() {
  const [url, setUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const run = async () => {
    if (!url.startsWith("http")) { toast.error("Enter full URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/seo/onpage", { url, target_keyword: keyword || null });
      setOut(data);
      toast.success(`Score: ${data.score} (${data.grade})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const gradeColor = (g) => {
    if (["A+", "A"].includes(g)) return "text-emerald-300";
    if (g === "B") return "text-indigo-300";
    if (g === "C") return "text-amber-300";
    return "text-rose-300";
  };

  return (
    <div className="space-y-4" data-testid="seo-onpage-panel">
      <div className="glass rounded-xl p-4 space-y-2">
        <div className="flex gap-2">
          <LinkIcon className="h-4 w-4 text-white/40 self-center" />
          <input value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://yoursite.com/page-to-audit"
            data-testid="seo-onpage-url"
            className="flex-1 bg-transparent text-sm outline-none" />
        </div>
        <div className="flex gap-2">
          <Hash className="h-4 w-4 text-white/40 self-center" />
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Target keyword (optional)" data-testid="seo-onpage-kw"
            className="flex-1 bg-transparent text-sm outline-none" />
          <button onClick={run} disabled={busy} data-testid="seo-onpage-run-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Audit
          </button>
        </div>
      </div>

      {out && (
        <div className="space-y-3">
          <div className="glass rounded-2xl p-6 flex items-center gap-6 flex-wrap">
            <div className={`font-heading text-7xl font-black ${gradeColor(out.grade)}`}>{out.grade}</div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">SEO score</div>
              <div className="font-heading text-3xl font-black">{out.score}<span className="text-white/30 text-lg">/100</span></div>
              <div className="text-xs text-white/40 mt-1 font-mono truncate max-w-md">{out.url}</div>
            </div>
            <div className="flex-1 grid grid-cols-3 gap-2 text-xs">
              <StatPill label="Words" value={out.stats.word_count} />
              <StatPill label="Title len" value={out.stats.title_length} />
              <StatPill label="Meta len" value={out.stats.meta_desc_length} />
              <StatPill label="H1s" value={out.stats.h1_count} />
              <StatPill label="Internal" value={out.stats.internal_links} />
              <StatPill label="Schema blocks" value={out.stats.schema_blocks} />
            </div>
          </div>

          <div className="glass rounded-xl divide-y divide-white/5">
            {out.checks.map((c, i) => (
              <div key={i} className="p-3 flex items-start gap-3 text-sm" data-testid={`seo-onpage-check-${i}`}>
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /> :
                        <AlertTriangle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold ${c.ok ? "" : "text-rose-200"}`}>{c.name}</div>
                  <div className="text-xs text-white/50 mt-0.5">{c.detail}</div>
                  {!c.ok && c.recommendation && (
                    <div className="text-xs text-indigo-300 mt-1">→ {c.recommendation}</div>
                  )}
                </div>
                <span className="text-xs text-white/30 font-mono">{c.weight}pt</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="glass rounded px-2 py-1 text-center">
      <div className="text-[10px] text-white/40 uppercase">{label}</div>
      <div className="font-mono text-sm">{value}</div>
    </div>
  );
}

function RankPanel() {
  const [keywords, setKeywords] = useState([]);
  const [form, setForm] = useState({ keyword: "", target_url: "", position: 1 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/seo/rank/keywords");
    setKeywords(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.keyword.trim() || !form.target_url.startsWith("http") || form.position < 1) {
      toast.error("Fill keyword, full URL, and position (1–100)"); return;
    }
    setSaving(true);
    try {
      await api.post("/seo/rank", { keyword: form.keyword.trim(),
        target_url: form.target_url.trim(), position: parseInt(form.position, 10) });
      toast.success("Rank logged");
      setForm({ keyword: "", target_url: "", position: 1 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (kw) => {
    if (!window.confirm(`Delete history for "${kw}"?`)) return;
    await api.delete(`/seo/rank/${encodeURIComponent(kw)}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-4" data-testid="seo-rank-panel">
      <div className="glass rounded-xl p-4 grid md:grid-cols-4 gap-2">
        <input value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })}
          placeholder="Target keyword" data-testid="seo-rank-keyword"
          className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })}
          placeholder="https://yoursite.com/page" data-testid="seo-rank-url"
          className="md:col-span-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <div className="flex gap-2">
          <input type="number" min="1" max="100" value={form.position}
            onChange={(e) => setForm({ ...form, position: e.target.value })}
            data-testid="seo-rank-position"
            className="w-20 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <button onClick={add} disabled={saving} data-testid="seo-rank-add-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Log
          </button>
        </div>
      </div>

      <div className="text-xs text-white/40">
        💡 Tip: pair this with your favourite SERP tracker (Ahrefs, SEMrush, free Bing rank checker) — log positions weekly and watch the trend.
      </div>

      {keywords.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-sm text-white/40">
          No keywords tracked yet. Add one above to start tracking.
        </div>
      ) : (
        <div className="space-y-2">
          {keywords.map((k) => {
            const Trend = k.delta > 0 ? TrendingUp : k.delta < 0 ? TrendingDown : Minus;
            const trendColor = k.delta > 0 ? "text-emerald-300" : k.delta < 0 ? "text-rose-300" : "text-white/40";
            return (
              <div key={k.keyword} className="glass rounded-xl p-4 flex items-center gap-4" data-testid={`seo-rank-row-${k.keyword}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{k.keyword}</div>
                  <a href={k.target_url} target="_blank" rel="noreferrer" className="text-xs text-white/40 truncate hover:text-white/60 inline-flex items-center gap-1">{k.target_url} <ExternalLink className="h-3 w-3" /></a>
                </div>
                <div className="text-center">
                  <div className="font-heading text-3xl font-black">#{k.position}</div>
                  <div className={`text-xs font-mono inline-flex items-center gap-1 ${trendColor}`}>
                    <Trend className="h-3 w-3" />{k.delta > 0 ? `+${k.delta}` : k.delta}
                  </div>
                </div>
                <div className="text-xs text-white/40 font-mono">{k.checks_count} checks</div>
                <button onClick={() => del(k.keyword)} data-testid={`seo-rank-del-${k.keyword}`}
                  className="p-2 rounded hover:bg-rose-500/10"><Trash2 className="h-4 w-4 text-rose-300" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
